(function () {
  const MOBILE_QUERY = '(max-width: 989px)';
  const MOBILE_COLUMNS = 4;
  const stateMap = new WeakMap();

  function isMobileLayout() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getVisibleCount() {
    return isMobileLayout() ? 8 : 7;
  }

  function needsMarquee(section) {
    const count = getLogoCount(section);
    if (isMobileLayout()) return count >= 8;
    return count > getVisibleCount();
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getTrack(section) {
    return section.querySelector('[data-champions-track]');
  }

  function getViewport(section) {
    return section.querySelector('[data-champions-viewport]');
  }

  function getToggle(section) {
    return section.querySelector('[data-champions-toggle]');
  }

  function getLogoCount(section) {
    const total = parseInt(section.dataset.logoCount, 10);
    if (!Number.isNaN(total) && total > 0) return total;

    return section.querySelectorAll('[data-champions-slide]:not([aria-hidden="true"])').length;
  }

  function getOriginalSlides(track) {
    return Array.from(track.querySelectorAll('[data-champions-slide]:not([aria-hidden="true"])'));
  }

  function getSlideOrder(slide) {
    const fromData = parseInt(slide.dataset.mbOrder, 10);
    if (!Number.isNaN(fromData)) return fromData;

    const fromStyle = parseInt(slide.style.getPropertyValue('--mb-order'), 10);
    return Number.isNaN(fromStyle) ? 0 : fromStyle;
  }

  function markCloneSlide(slide) {
    slide.setAttribute('aria-hidden', 'true');
    slide.removeAttribute('data-shopify-editor-block');

    const link = slide.querySelector('a');
    if (link) link.setAttribute('tabindex', '-1');

    slide.querySelectorAll('img').forEach((img) => {
      img.setAttribute('alt', '');
      img.setAttribute('aria-hidden', 'true');
    });
  }

  function cloneSlide(slide) {
    const clone = slide.cloneNode(true);
    markCloneSlide(clone);
    return clone;
  }

  function createRow() {
    const row = document.createElement('ul');
    row.className = 'home-champions__row';
    row.setAttribute('data-champions-row', '');
    row.setAttribute('role', 'list');
    return row;
  }

  function resetTrack(section) {
    const track = getTrack(section);
    if (!track) return;

    const originals = getOriginalSlides(track);
    originals.forEach((slide) => track.appendChild(slide));
    track.querySelectorAll('[data-champions-group]').forEach((group) => group.remove());
    track.querySelectorAll('[data-champions-slide][aria-hidden="true"]').forEach((clone) => {
      clone.remove();
    });
    track.dataset.championsDuplicated = 'false';
    track.classList.remove('is-marquee-ready');
    track.style.removeProperty('animation');
    track.style.removeProperty('transform');
  }

  function duplicateDesktop(track, slides) {
    slides.forEach((slide) => track.appendChild(cloneSlide(slide)));
  }

  function duplicateMobile(track, slides) {
    const sorted = slides.slice().sort((a, b) => getSlideOrder(a) - getSlideOrder(b));
    const row1 = createRow();
    const row2 = createRow();

    sorted.forEach((slide, index) => {
      const row = Math.floor(index / MOBILE_COLUMNS) % 2 === 0 ? row1 : row2;
      row.appendChild(slide);
    });

    const rowCount = Math.max(row1.children.length, row2.children.length);
    [row1, row2].forEach((row) => {
      while (row.children.length && row.children.length < rowCount) {
        const spacer = document.createElement('li');
        spacer.className = 'home-champions__item home-champions__item--spacer';
        spacer.setAttribute('aria-hidden', 'true');
        row.appendChild(spacer);
      }
    });

    const group = document.createElement('li');
    group.className = 'home-champions__group';
    group.setAttribute('data-champions-group', '');
    if (row1.children.length) group.appendChild(row1);
    if (row2.children.length) group.appendChild(row2);
    track.appendChild(group);

    const cloneGroup = group.cloneNode(true);
    cloneGroup.setAttribute('aria-hidden', 'true');
    cloneGroup.querySelectorAll('[data-champions-slide]').forEach(markCloneSlide);
    track.appendChild(cloneGroup);
  }

  function duplicateTrack(section) {
    const track = getTrack(section);
    if (!track) return;

    resetTrack(section);
    const slides = getOriginalSlides(track);
    if (!slides.length) return;

    if (isMobileLayout()) {
      duplicateMobile(track, slides);
    } else {
      duplicateDesktop(track, slides);
    }

    track.dataset.championsDuplicated = 'true';
  }

  function getState(section) {
    if (!stateMap.has(section)) {
      stateMap.set(section, {
        isPlaying: false,
        resumeOnFocusOut: false,
        resumeOnVisible: false,
        bound: false,
      });
    }
    return stateMap.get(section);
  }

  function setMarqueeDuration(section) {
    const track = getTrack(section);
    if (!track) return;

    const desktopMs = parseInt(section.dataset.marqueeDuration, 10) || 40000;
    const mobileMs = parseInt(section.dataset.marqueeDurationMb, 10) || desktopMs;
    const durationMs = isMobileLayout() ? mobileMs : desktopMs;
    track.style.setProperty('--hc-marquee-duration', `${durationMs}ms`);
  }

  function updateToggleState(section, isPlaying) {
    const toggle = getToggle(section);
    if (!toggle) return;

    const strings = window.accessibilityStrings || {};
    toggle.classList.toggle('home-champions__toggle--paused', !isPlaying);
    toggle.setAttribute('aria-pressed', isPlaying ? 'false' : 'true');
    toggle.setAttribute(
      'aria-label',
      isPlaying
        ? strings.pauseSlideshow || 'Pause slideshow'
        : strings.playSlideshow || 'Play slideshow'
    );
  }

  function updateLayoutClass(section) {
    section.classList.toggle('home-champions__carousel--stacked', isMobileLayout());
  }

  function play(section, options) {
    const opts = options || {};
    const state = getState(section);
    const track = getTrack(section);
    const viewport = getViewport(section);

    if (!needsMarquee(section) || !track) return;

    track.classList.remove('is-paused');
    state.isPlaying = true;
    if (viewport) viewport.setAttribute('aria-live', 'off');
    updateToggleState(section, true);

    if (!opts.silent) {
      state.resumeOnFocusOut = false;
      state.resumeOnVisible = false;
    }
  }

  function pause(section, options) {
    const opts = options || {};
    const state = getState(section);
    const track = getTrack(section);
    const viewport = getViewport(section);

    if (track) track.classList.add('is-paused');
    state.isPlaying = false;
    if (viewport) viewport.setAttribute('aria-live', 'polite');
    updateToggleState(section, false);

    if (opts.resumeOnFocusOut) state.resumeOnFocusOut = true;
    if (opts.resumeOnVisible) state.resumeOnVisible = true;
  }

  function togglePlayback(section) {
    const state = getState(section);
    if (state.isPlaying) {
      pause(section);
      state.resumeOnFocusOut = false;
      state.resumeOnVisible = false;
    } else {
      play(section);
    }
  }

  function updateStaticState(section) {
    section.classList.toggle('home-champions__carousel--static', !needsMarquee(section));
  }

  function syncLayout(section) {
    const state = getState(section);
    updateLayoutClass(section);
    updateStaticState(section);
    setMarqueeDuration(section);

    if (!needsMarquee(section)) {
      resetTrack(section);
      pause(section);
      return;
    }

    duplicateTrack(section);

    const track = getTrack(section);
    if (track && !prefersReducedMotion()) {
      track.classList.add('is-marquee-ready');
      if (state.isPlaying || !state.bound) {
        play(section, { silent: true });
      }
    } else {
      pause(section);
    }
  }

  function handleResize(section) {
    const state = getState(section);
    const wasPlaying = state.isPlaying;
    syncLayout(section);
    if (wasPlaying && needsMarquee(section) && !prefersReducedMotion()) {
      play(section, { silent: true });
    }
  }

  function bindEvents(section) {
    const state = getState(section);
    if (state.bound) return;

    state.bound = true;

    const toggleBtn = getToggle(section);
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => togglePlayback(section));
    }

    section.addEventListener('focusin', (event) => {
      if (!needsMarquee(section)) return;

      const focusedToggle =
        toggleBtn && (event.target === toggleBtn || toggleBtn.contains(event.target));

      if (focusedToggle) {
        if (state.isPlaying) play(section);
        return;
      }

      if (state.isPlaying) {
        pause(section, { resumeOnFocusOut: true });
      }
    });

    section.addEventListener('focusout', (event) => {
      if (!needsMarquee(section) || !state.resumeOnFocusOut) return;

      const nextTarget = event.relatedTarget;
      if (nextTarget && section.contains(nextTarget)) return;

      state.resumeOnFocusOut = false;
      play(section, { silent: true });
    });

    section.addEventListener('mouseenter', () => {
      if (!needsMarquee(section) || !state.isPlaying) return;
      pause(section, { resumeOnFocusOut: true });
    });

    section.addEventListener('mouseleave', () => {
      if (!needsMarquee(section) || !state.resumeOnFocusOut) return;
      state.resumeOnFocusOut = false;
      play(section, { silent: true });
    });

    if (!window.__homeChampionsResizeBound) {
      window.__homeChampionsResizeBound = true;
      window.addEventListener('resize', () => {
        document.querySelectorAll('[data-home-champions]').forEach(handleResize);
      });
    }

    if (!window.__homeChampionsVisibilityBound) {
      window.__homeChampionsVisibilityBound = true;
      document.addEventListener('visibilitychange', () => {
        document.querySelectorAll('[data-home-champions]').forEach((carousel) => {
          const carouselState = getState(carousel);
          if (document.hidden) {
            if (carouselState.isPlaying) {
              pause(carousel, { resumeOnVisible: true });
            }
          } else if (carouselState.resumeOnVisible) {
            carouselState.resumeOnVisible = false;
            play(carousel, { silent: true });
          }
        });
      });
    }
  }

  function initSection(section) {
    if (!section || section.dataset.championsInitialized === 'true') return;

    section.dataset.championsInitialized = 'true';
    bindEvents(section);
    syncLayout(section);

    if (needsMarquee(section) && !prefersReducedMotion()) {
      play(section);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-home-champions]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-home-champions]');
    if (!section) return;

    resetTrack(section);
    section.dataset.championsInitialized = 'false';
    initSection(section);
  });
})();
